import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const DEFAULT_COORDINATES = {
  latitude: 16.5062,
  longitude: 80.648,
};

const LocationContext = createContext(undefined);

export const LocationProvider = ({ children }) => {
  const [coordinates, setCoordinates] = useState(DEFAULT_COORDINATES);
  const [address, setAddress] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState(null);

  const detectCurrentLocation = useCallback(async () => {
    setIsDetecting(true);
    setError(null);

    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const err = 'Geolocation is not supported by your browser';
        setError(err);
        setIsDetecting(false);
        reject(new Error(err));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const newCoords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setCoordinates(newCoords);

          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${newCoords.latitude}&lon=${newCoords.longitude}`
            );
            if (res.ok) {
              const data = await res.json();
              const addr = data.address || {};
              const primaryLine = [addr.house_number, addr.road || addr.pedestrian || addr.suburb]
                .filter(Boolean)
                .join(' ');

              setAddress({
                addressLine1: primaryLine,
                street: addr.road || addr.pedestrian || addr.suburb || '',
                addressLine2: addr.neighbourhood || addr.city_district || addr.county || '',
                city: addr.city || addr.town || addr.village || addr.hamlet || 'Andhra Pradesh',
                state: addr.state || 'Andhra Pradesh',
                postalCode: addr.postcode || '',
                displayName: data.display_name,
              });
            }
          } catch {
            // Ignore reverse geocode network issues
          }

          setIsDetecting(false);
          resolve(newCoords);
        },
        (geoErr) => {
          setIsDetecting(false);
          setError(geoErr.message || 'Failed to detect location');
          reject(geoErr);
        },
        { timeout: 10000, enableHighAccuracy: true }
      );
    });
  }, []);

  const setManualLocation = useCallback((coords, addr) => {
    setCoordinates(coords);
    if (addr) setAddress(addr);
    setError(null);
  }, []);

  const value = useMemo(
    () => ({
      coordinates,
      address,
      isDetecting,
      error,
      detectCurrentLocation,
      setManualLocation,
    }),
    [coordinates, address, isDetecting, error, detectCurrentLocation, setManualLocation]
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

export const useLocationState = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocationState must be used within a LocationProvider');
  }
  return context;
};

export default LocationContext;
